import cn from 'classnames';
import { useComputed } from '@preact/signals';
import type { MouseEventHandler } from 'react';
import { Button } from '@nespresso-design-system/components';
import ProductListHeroBannerModal from './modals/ProductListHeroBannerModal/ProductListHeroBannerModal.tsx';
import type { Config, HeroBannerCTA, TechOptions } from './config';
import styles from './ProductListHeroBanner.module.scss';
import Providers from './Providers.tsx';
import SkeletonShape from '@/widgets/components/controls/Skeleton/SkeletonShape.tsx';
import useQueryStringStore from '@/widgets/hooks/useQueryStringStore.tsx';
import useWidgetOptions from '@/widgets/hooks/useWidgetOptions.tsx';
import useImpression from '@/widgets/hooks/useImpression.tsx';
import type { PersistedState } from './Store.ts';
import type { PLPRoute } from '@/domain/Route.ts';
import type { WidgetConfig } from '@/domain/WidgetConfig.ts';
import {
  RAISED_BY,
  trackSelectPromotion
} from '@/widgets/services/TrackingService.ts';

const CREATIVE_SLOT = 'global_plp_v3_hero_banner';

export type ProductListHeroBannerProps = {
  config: Config | WidgetConfig;
  route: PLPRoute;
};

export default function ProductListHeroBanner({
  config,
  route
}: ProductListHeroBannerProps) {
  return (
    <Providers config={config} route={route}>
      <ProductListHeroBannerLayout />
    </Providers>
  );
}

function ProductListHeroBannerLayout() {
  const { appearance, cta, images, modal, tracking } =
    useWidgetOptions<TechOptions>();
  const { ctaType, ctaLabel, ctaTarget, ctaLink } = cta as HeroBannerCTA;

  const bannerRef = useImpression<HTMLDivElement>(
    RAISED_BY,
    CREATIVE_SLOT,
    tracking
  );

  const [{ openModal }, setPersistedState] =
    useQueryStringStore<PersistedState>();

  const isOpen = useComputed(
    () => openModal.value === 'product-list-hero-banner'
  );

  const hasModal = ctaType === 'modal';
  const hasCTA = hasModal || ctaType === 'link';

  if (!appearance?.heading || !images?.desktopImage) {
    return null;
  }

  const theme =
    appearance.backgroundTheme === 'light' ? styles.light : styles.dark;
  const { mainHeading, heading, smallHeading } = appearance;

  const onCTA: MouseEventHandler<HTMLAnchorElement> = (e) => {
    trackSelectPromotion({
      ...tracking,
      ctaName: `LINK - 1 - ${ctaLabel}`,
      creativeSlot: CREATIVE_SLOT
    });

    if (hasModal) {
      e.preventDefault();
      setPersistedState({ openModal: 'product-list-hero-banner' });
    }
  };

  return (
    <div class={styles.container}>
      {mainHeading && <h1 class={styles.mainHeading}>{mainHeading}</h1>}

      <div
        ref={bannerRef}
        class={cn(styles.heroBanner, theme)}
        style={{
          '--hero-banner-image-desktop': `url(${images.desktopImage})`,
          '--hero-banner-image-tablet': `url(${images.tabletImage})`,
          '--hero-banner-image-mobile': `url(${images.mobileImage})`
        }}
      >
        <div class={styles.info}>
          <p class={styles.smallHeading}>{smallHeading}</p>
          <p class={styles.heading}>{heading}</p>
          {hasCTA && (
            <Button
              target={ctaTarget}
              href={ctaLink}
              contrast={appearance.backgroundTheme}
              className={styles.cta}
              label={ctaLabel}
              onClick={onCTA}
              variant="secondary"
            />
          )}
        </div>

        {hasModal && (
          <ProductListHeroBannerModal
            {...modal}
            isOpen={isOpen.value}
            onClose={() => setPersistedState({ openModal: undefined })}
          />
        )}
      </div>
    </div>
  );
}

ProductListHeroBanner.Skeleton = function Skeleton() {
  return (
    <div class={styles.container}>
      <SkeletonShape class={styles.skeleton} />
    </div>
  );
};
